import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { vehiclesAPI, driversAPI, clientsAPI, auditLogsAPI, workstreamAPI, sitesAPI } from '../services/api';
import { formatDateBritish, formatDateTimeBritish } from '../utils/dateUtils';
import './Dashboard.css';
import driverIcon from '../assets/icons/driver-main-dash.png';
import totalVehiclesIcon from '../assets/icons/total-vehicles-dash.png';
import vehicleIcon from '../assets/icons/vehicle.png';
import additionIcon from '../assets/icons/addition.png';
import clientIcon from '../assets/icons/client.png';
import teamIcon from '../assets/icons/team.png';
import clipboardIcon from '../assets/icons/clipboard.png';

interface Activity {
  id: number;
  entityType: string;
  entityId: number;
  action: string;
  timestamp: string;
  performedBy: string;
  description: string;
  clientId?: number;
  vehicleRegistration?: string;
  driverName?: string;
  clientName?: string;
}

const Dashboard: React.FC = () => {
  const { user, isAdmin: isAdminRole, isMTA, isFleetManager } = useAuth();
  const navigate = useNavigate();
  const isOnboarder = user?.role === 'Onboarder';
  const isAdmin = isAdminRole || isMTA;
  const [stats, setStats] = useState({
    vehicles: 0,
    drivers: 0,
    clients: 0,
    activeVehicles: 0,
    inactiveVehicles: 0,
    pendingCoverVehicles: 0,
    coverNotices: 0,
    pendingVehicles: 0,
    pendingReinstatements: 0,
    pendingRemovals: 0,
    totalPendingActions: 0,
    pendingDriverReferrals: 0,
    loading: true,
  });
  const [recentActivity, setRecentActivity] = useState<Activity[]>([]);
  const [loadingActivity, setLoadingActivity] = useState(true);
  const [pendingReferralsList, setPendingReferralsList] = useState<any[]>([]);
  const [siteVehicleBreakdown, setSiteVehicleBreakdown] = useState<{siteName: string; siteId: number | null; onCover: number; pending: number; perm: number; temp: number; total: number}[]>([]);
  const [dashSiteFilter, setDashSiteFilter] = useState<number | null>(null);
  const [siteList, setSiteList] = useState<{id: number; name: string}[]>([]);

  useEffect(() => {
    loadStats();
    loadRecentActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadStats = async () => {
    // Use allSettled so a single failing endpoint never zeroes-out all stats.
    const settled = (r: PromiseSettledResult<any>) =>
      r.status === 'fulfilled' ? r.value : null;

    try {
      if (isFleetManager && user?.clientId) {
        // Fleet manager: single parallel batch using only client-specific data.
        // Avoids the slow global getStats() call entirely.
        const [unreadSettled, referralsSettled, vehicleListSettled, sitesSettled] = await Promise.allSettled([
          workstreamAPI.getUnreadCount(user.clientId),
          driversAPI.getPendingReferrals(),
          vehiclesAPI.getByClient(user.clientId),
          sitesAPI.getByClient(user.clientId),
        ]);
        const unreadRes      = settled(unreadSettled);
        const referralsRes   = settled(referralsSettled);
        const vehicleListRes = settled(vehicleListSettled);
        const sitesRes       = settled(sitesSettled);
        setPendingReferralsList(referralsRes?.data ?? []);

        // Compute vehicle stats from the client-specific list.
        // Mirrors VehicleList's filter: isActive=true AND coverStatus != 'Inactive'.
        // Vehicles with isActive=true but CoverStatus='Inactive' are genuine removals
        // that should not appear in live fleet counts.
        const allVehicles: any[] = (vehicleListRes?.data ?? []).filter((v: any) =>
          v.isActive !== false && v.coverStatus !== 'Inactive'
        );
        const activeVehicles       = allVehicles.filter((v: any) => v.coverStatus === 'Active').length;
        const inactiveVehicles     = allVehicles.filter((v: any) => v.coverStatus === 'Inactive').length;
        const pendingCoverVehicles = allVehicles.filter((v: any) =>
          v.coverStatus === 'Cover Pending' || v.coverStatus === 'Pending Cover').length;
        const totalVehicles = allVehicles.length;

        // Build site breakdown
        const clientSites: {id: number; name: string}[] = sitesRes?.data ?? [];
        setSiteList(clientSites);
        const siteMap = new Map<number | null, {siteName: string; siteId: number | null; onCover: number; pending: number; perm: number; temp: number; total: number}>();
        clientSites.forEach((s: {id: number; name: string}) => {
          siteMap.set(s.id, { siteName: s.name, siteId: s.id, onCover: 0, pending: 0, perm: 0, temp: 0, total: 0 });
        });
        siteMap.set(null, { siteName: 'Head Office', siteId: null, onCover: 0, pending: 0, perm: 0, temp: 0, total: 0 });
        allVehicles.forEach((v: any) => {
          const key = v.siteId ?? null;
          if (!siteMap.has(key)) siteMap.set(key, { siteName: 'Head Office', siteId: null, onCover: 0, pending: 0, perm: 0, temp: 0, total: 0 });
          const entry = siteMap.get(key)!;
          entry.total += 1;
          if (v.coverStatus === 'Active') entry.onCover += 1;
          else if (v.coverStatus === 'Cover Pending' || v.coverStatus === 'Pending Cover') entry.pending += 1;
          if (v.tempCoverInfo) entry.temp += 1;
          else entry.perm += 1;
        });
        setSiteVehicleBreakdown(Array.from(siteMap.values()).filter(e => e.total > 0));

        setStats({
          vehicles: totalVehicles, drivers: 0, clients: 0,
          coverNotices: unreadRes?.data || 0,
          pendingDriverReferrals: 0, pendingVehicles: 0,
          pendingReinstatements: 0, pendingRemovals: 0, totalPendingActions: 0,
          activeVehicles, inactiveVehicles, pendingCoverVehicles,
          loading: false,
        });
        return;
      }

      // Non-fleet-manager: single parallel batch per user type — no sequential waits
      if (isAdmin) {
        // Admin: all 6 calls in one parallel round-trip
        const [vehiclesSettled, driversSettled, clientsSettled, pendingSettled, reinstatementSettled, removalSettled] =
          await Promise.allSettled([
            vehiclesAPI.getStats(),
            driversAPI.getCount(),
            clientsAPI.getAll(),
            workstreamAPI.getPending(),
            workstreamAPI.getReinstatementRequests(),
            workstreamAPI.getRemovalRequests(),
          ]);
        const vehiclesRes       = settled(vehiclesSettled);
        const driversRes        = settled(driversSettled);
        const clientsRes        = settled(clientsSettled);
        const pendingRes        = settled(pendingSettled);
        const reinstatementRes  = settled(reinstatementSettled);
        const removalRes        = settled(removalSettled);
        const vehicleStatsList: Array<{status: string; count: number}> = vehiclesRes?.data ?? [];
        const activeVehicles       = vehicleStatsList.find(s => s.status === 'Active')?.count || 0;
        const inactiveVehicles     = vehicleStatsList.find(s => s.status === 'Inactive')?.count || 0;
        const pendingCoverVehicles =
          (vehicleStatsList.find(s => s.status === 'Cover Pending')?.count || 0) +
          (vehicleStatsList.find(s => s.status === 'Pending Cover')?.count || 0);
        const totalVehicles        = vehicleStatsList.reduce((sum, s) => sum + s.count, 0);
        const driverCount: number  = driversRes?.data?.count ?? 0;
        const pendingVehicles      = pendingRes?.data?.length       || 0;
        const pendingReinstatements = reinstatementRes?.data?.length || 0;
        const pendingRemovals      = removalRes?.data?.length       || 0;
        setStats({
          vehicles: totalVehicles, drivers: driverCount,
          clients: clientsRes?.data?.length || 0,
          coverNotices: 0, pendingDriverReferrals: 0,
          pendingVehicles, pendingReinstatements, pendingRemovals,
          totalPendingActions: pendingVehicles + pendingReinstatements + pendingRemovals,
          activeVehicles, inactiveVehicles, pendingCoverVehicles,
          loading: false,
        });
      } else {
        // Client: all 4 calls in one parallel round-trip
        const [vehiclesSettled, driversSettled, unreadSettled, referralsSettled] =
          await Promise.allSettled([
            vehiclesAPI.getStats(),
            driversAPI.getCount(),
            workstreamAPI.getUnreadCount(user?.clientId || 0),
            driversAPI.getPendingReferrals(),
          ]);
        const vehiclesRes  = settled(vehiclesSettled);
        const driversRes   = settled(driversSettled);
        const unreadRes    = settled(unreadSettled);
        const referralsRes = settled(referralsSettled);
        const vehicleStatsList: Array<{status: string; count: number}> = vehiclesRes?.data ?? [];
        const activeVehicles       = vehicleStatsList.find(s => s.status === 'Active')?.count || 0;
        const inactiveVehicles     = vehicleStatsList.find(s => s.status === 'Inactive')?.count || 0;
        const pendingCoverVehicles =
          (vehicleStatsList.find(s => s.status === 'Cover Pending')?.count || 0) +
          (vehicleStatsList.find(s => s.status === 'Pending Cover')?.count || 0);
        const totalVehicles = vehicleStatsList.reduce((sum, s) => sum + s.count, 0);
        const driverCount: number = driversRes?.data?.count ?? 0;
        setPendingReferralsList(referralsRes?.data ?? []);
        setStats({
          vehicles: totalVehicles, drivers: driverCount, clients: 0,
          coverNotices: unreadRes?.data || 0,
          pendingDriverReferrals: 0, pendingVehicles: 0,
          pendingReinstatements: 0, pendingRemovals: 0, totalPendingActions: 0,
          activeVehicles, inactiveVehicles, pendingCoverVehicles,
          loading: false,
        });
      }
    } catch (error) {
      console.error('Error loading stats:', error);
      setStats(prev => ({ ...prev, loading: false }));
    }
  };

  const loadRecentActivity = async () => {
    try {
      const response = await auditLogsAPI.getRecent(10);
      setRecentActivity(response.data);
    } catch (error) {
      console.error('Error loading recent activity:', error);
    } finally {
      setLoadingActivity(false);
    }
  };

  const getActivityIcon = (action: string) => {
    if (action === 'Create') {
      return <img src={additionIcon} alt="Added" style={{width: '24px', height: '24px'}} />;
    }
    if (action === 'Delete') {
      return <span style={{fontSize: '24px', color: '#dc3545'}}>✖</span>;
    }
    return <span style={{fontSize: '24px'}}>•</span>;
  };

  if (stats.loading) {
    return <div>Loading dashboard...</div>;
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Welcome back, <span style={{color: '#FFA500'}}>{user?.firstName}</span>!</h1>
        <p>Here's what's happening with your fleet</p>
        {isOnboarder && <button className="btn btn-primary" onClick={() => navigate('/clients?onboard=true')}>+ Onboard Client</button>}
      </div>

      <div className="stats-grid">
        {isAdmin ? (
          <>
            <div className="stat-card stat-workstream clickable" onClick={() => navigate('/workstream')}>
              {stats.totalPendingActions > 0 && (
                <div className="notification-badge">{stats.totalPendingActions}</div>
              )}
              <div className="stat-icon"><img src={teamIcon} alt="Workstream" className="stat-icon-img" /></div>
              <div className="stat-info">
                <h3>{stats.totalPendingActions}</h3>
                <p>Pending Actions</p>
              </div>
            </div>

            <div className="stat-card stat-clients clickable" onClick={() => navigate('/clients')}>
              <div className="stat-icon"><img src={clientIcon} alt="Clients" className="stat-icon-img" /></div>
              <div className="stat-info">
                <h3>{stats.clients}</h3>
                <p>Search Clients</p>
              </div>
            </div>

            <div className="stat-card stat-vehicles clickable" onClick={() => navigate('/vehicles')}>
              <div className="stat-icon"><img src={vehicleIcon} alt="Vehicles" className="stat-icon-img" /></div>
              <div className="stat-info">
                <h3>{stats.vehicles}</h3>
                <p>Search Vehicles</p>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="stat-card stat-active clickable" onClick={() => navigate('/vehicles')}>
              <div className="stat-icon"><img src={vehicleIcon} alt="Vehicles On Cover" className="stat-icon-img" /></div>
              <div className="stat-info">
                <h3>{stats.activeVehicles}</h3>
                <p>Vehicles On Cover</p>
              </div>
            </div>

            <div className="stat-card stat-vehicles clickable" onClick={() => navigate('/vehicles')}>
              <div className="stat-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#1a202c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 3h14M5 21h14M7 3v4l5 5-5 5v4M17 3v4l-5 5 5 5v4"/>
                </svg>
              </div>
              <div className="stat-info">
                <h3>{stats.pendingCoverVehicles}</h3>
                <p>Cover Pending</p>
              </div>
            </div>

            <div className="stat-card stat-drivers clickable" onClick={() => navigate('/drivers')}>
              <div className="stat-icon"><img src={driverIcon} alt="Drivers" className="stat-icon-img" /></div>
              <div className="stat-info">
                <h3>{stats.drivers}</h3>
                <p>Total Named Drivers Outside Warranty</p>
              </div>
            </div>

            <div className="stat-card stat-cover clickable" onClick={() => navigate('/messages')}>
              {stats.coverNotices > 0 && (
                <div className="notification-badge">{stats.coverNotices}</div>
              )}
              <div className="stat-icon"><img src={clipboardIcon} alt="My Notices" className="stat-icon-img" /></div>
              <div className="stat-info">
                <h3>{stats.coverNotices}</h3>
                <p>My Notices</p>
              </div>
            </div>
          </>
        )}
      </div>

      {isFleetManager && siteVehicleBreakdown.length > 0 && (
        <div className="recent-activity" style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0 }}>Fleet by Site</h2>
            <select
              value={dashSiteFilter ?? ''}
              onChange={(e) => setDashSiteFilter(e.target.value === '' ? null : Number(e.target.value))}
              style={{ padding: '6px 12px', borderRadius: 6, border: '1.5px solid #e2e8f0', fontSize: 14 }}
            >
              <option value="">All Sites</option>
              {siteList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              {siteVehicleBreakdown.some(entry => entry.siteId === null && entry.total > 0) && (
                <option value="-1">Head Office</option>
              )}
            </select>
          </div>
          {(() => {
            const rows = siteVehicleBreakdown
              .filter(entry => dashSiteFilter === null || entry.siteId === dashSiteFilter || (dashSiteFilter === -1 && entry.siteId === null));
            const totals = rows.reduce((acc, e) => ({
              onCover: acc.onCover + e.onCover,
              perm: acc.perm + e.perm,
              temp: acc.temp + e.temp,
              total: acc.total + e.total,
            }), { onCover: 0, perm: 0, temp: 0, total: 0 });
            const thStyle: React.CSSProperties = { textAlign: 'center', padding: '8px 12px', color: '#374151', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' };
            return (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                      <th style={{ textAlign: 'left', padding: '8px 12px', color: '#374151', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Site</th>
                      <th style={{ ...thStyle }} title="All approved vehicles currently on cover — includes both permanent and temporary vehicles">
                        On Cover<br/>
                        <span style={{ fontSize: 10, fontWeight: 400, color: '#6b7280', textTransform: 'none', letterSpacing: 0 }}>(perm + temp)</span>
                      </th>
                      <th style={thStyle}>Perm</th>
                      <th style={thStyle}>Temp</th>
                      <th style={{ ...thStyle, borderLeft: '2px solid #e2e8f0' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((entry, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '8px 12px', fontWeight: 600 }}>{entry.siteName}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', color: '#16a34a', fontWeight: 600 }}>{entry.onCover}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', color: '#374151' }}>{entry.perm}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', color: '#3b82f6' }}>{entry.temp}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 700, borderLeft: '2px solid #e2e8f0' }}>
                          {entry.total}{entry.total !== entry.onCover && (
                            <span style={{ fontSize: 11, color: '#d97706', marginLeft: 4 }} title={`${entry.total - entry.onCover} vehicle(s) pending or awaiting inception`}>
                              (+{entry.total - entry.onCover} pending)
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: '#002060', borderTop: '2px solid #002060' }}>
                      <td style={{ padding: '9px 12px', fontWeight: 700, color: '#fff', fontSize: 13 }}>Totals</td>
                      <td style={{ padding: '9px 12px', textAlign: 'center', fontWeight: 700, color: '#6ee7b7' }}>{totals.onCover}</td>
                      <td style={{ padding: '9px 12px', textAlign: 'center', fontWeight: 700, color: '#fff' }}>{totals.perm}</td>
                      <td style={{ padding: '9px 12px', textAlign: 'center', fontWeight: 700, color: '#93c5fd' }}>{totals.temp}</td>
                      <td style={{ padding: '9px 12px', textAlign: 'center', fontWeight: 700, color: '#fff', borderLeft: '2px solid #1e3a6e' }}>{totals.total}</td>
                    </tr>
                  </tfoot>
                </table>
                {totals.total !== totals.onCover && (
                  <p style={{ margin: '6px 0 0', fontSize: 12, color: '#d97706' }}>
                    &#9888; {totals.total - totals.onCover} vehicle(s) are not yet active — pending cover approval or pending removal. On Cover already includes all approved temp vehicles.
                  </p>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {!isAdmin && pendingReferralsList.length > 0 && (
        <div className="recent-activity" style={{ marginBottom: 24 }}>
          <h2>Pending Referred Named Drivers ({pendingReferralsList.length})</h2>
          <div className="activity-list">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: '#374151', fontWeight: 600 }}>Name</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: '#374151', fontWeight: 600 }}>Email</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: '#374151', fontWeight: 600 }}>Phone</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: '#374151', fontWeight: 600 }}>Submitted</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: '#374151', fontWeight: 600 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {pendingReferralsList.map((driver: any) => (
                  <tr key={driver.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '8px 12px' }}><strong>{driver.firstName} {driver.lastName}</strong></td>
                    <td style={{ padding: '8px 12px', color: '#4a5568' }}>{driver.email || '—'}</td>
                    <td style={{ padding: '8px 12px', color: '#4a5568' }}>{driver.phone || '—'}</td>
                    <td style={{ padding: '8px 12px', color: '#718096', fontSize: 13 }}>{driver.createdAt ? formatDateBritish(driver.createdAt) : '—'}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 9999, fontSize: 12, fontWeight: 600 }}>Pending Approval</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="recent-activity">
        <h2>Recent Activity</h2>
        {loadingActivity ? (
          <p>Loading activity...</p>
        ) : recentActivity.length === 0 ? (
          <p className="empty-state">No recent activity to display.</p>
        ) : (
          <div className="activity-list">
            {recentActivity.map((activity) => (
              <div key={activity.id} className="activity-item">
                <div className="activity-icon">{getActivityIcon(activity.action)}</div>
                <div className="activity-details">
                  <p className="activity-description">{activity.description}</p>
                  <p className="activity-meta">
                    <span className="activity-time">{formatDateTimeBritish(activity.timestamp)}</span>
                    {activity.performedBy && (
                      <span className="activity-user">
                        {' • by '}{activity.performedBy}
                        {activity.clientName && (
                          <span style={{color: '#718096'}}>
                            {activity.description?.includes('on behalf of') ? ' on behalf of ' : ' ('}
                            {activity.clientName}
                            {activity.description?.includes('on behalf of') ? '' : ')'}
                          </span>
                        )}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
